import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ListChecks, Search, RefreshCw, Calendar, User, AlertCircle, Package, ChevronDown, ChevronRight, Loader, Check, X, Edit3, Trash2 } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { PListDto, PListDetailsDto } from '../../../../types/picking';
import { BackButton } from '../../../common/BackButton';

interface EditableHeaderData {
  description1: string;
  description2: string;
  priority: number;
  operatorID: string;
  deliveryDate: string; // ISO date string
}

interface HeaderEditState {
  [key: string]: EditableHeaderData; // key: "pListId"
}

interface HeaderSaveState {
  [key: string]: 'saved' | 'saving' | 'error'; // key: "pListId"
}

const EDITABLE_FIELDS = ['description1', 'description2', 'priority', 'operatorID', 'deliveryDate'] as const;
type EditableField = typeof EDITABLE_FIELDS[number];

interface WorkingPickingListsProps {
  onBack?: () => void;
}

const WorkingPickingLists: React.FC<WorkingPickingListsProps> = ({ onBack }) => {
  const [pickingLists, setPickingLists] = useState<PListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLists, setExpandedLists] = useState<Set<number>>(new Set());
  const [listDetails, setListDetails] = useState<Map<number, PListDetailsDto>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());
  
  // Editing state
  const [focusedCell, setFocusedCell] = useState<string | null>(null); // "pListId-field"
  const [editState, setEditState] = useState<HeaderEditState>({});
  const [changedHeaders, setChangedHeaders] = useState<Set<string>>(new Set()); // "pListId"
  const [saveState, setSaveState] = useState<HeaderSaveState>({});

  // Delete state
  const [deletingPListId, setDeletingPListId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pListToDelete, setPListToDelete] = useState<PListDto | null>(null);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    loadPickingLists();
  }, []);

  const loadPickingLists = async () => {
    try {
      setLoading(true);
      setError(null);
      // Hent plukklister med statuser: 10, 20, 40, 45, 50
      const data = await pickingService.getPickingListsByStatuses([10, 20, 40, 45, 50]);
      setPickingLists(data);
      
      // Initialize edit state for all headers
      const newEditState: HeaderEditState = {};
      data.forEach(pickList => {
        const key = `${pickList.pListID}`;
        newEditState[key] = {
          description1: pickList.description1 || '',
          description2: pickList.description2 || '',
          priority: pickList.priority,
          operatorID: pickList.operatorID || '',
          deliveryDate: pickList.deliveryDate ? new Date(pickList.deliveryDate).toISOString().split('T')[0] : ''
        };
      });
      setEditState(newEditState);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Feil ved lasting av plukklister');
      console.error('Error loading picking lists:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleListExpansion = async (pListId: number) => {
    const newExpanded = new Set(expandedLists);
    
    if (newExpanded.has(pListId)) {
      newExpanded.delete(pListId);
      setExpandedLists(newExpanded);
    } else {
      newExpanded.add(pListId);
      setExpandedLists(newExpanded);
      
      if (!listDetails.has(pListId)) {
        await loadPickingListDetails(pListId);
      }
    }
  };

  const loadPickingListDetails = async (pListId: number) => {
    try {
      setLoadingDetails(prev => new Set(prev).add(pListId));
      const details = await pickingService.getPickingDetails(pListId);
      setListDetails(prev => new Map(prev).set(pListId, details));
    } catch (err) {
      console.error(`Error loading details for picking list ${pListId}:`, err);
      alert(`Feil ved lasting av detaljer: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  };

  const getHeaderKey = (pListId: number) => `${pListId}`;
  const getCellKey = (pListId: number, field: string) => `${pListId}-${field}`;

  const handleCellFocus = (pListId: number, field: string) => {
    const cellKey = getCellKey(pListId, field);
    setFocusedCell(cellKey);
    
    // Focus and select the input
    setTimeout(() => {
      const input = inputRefs.current[cellKey];
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  };

  const handleCellChange = (pListId: number, field: keyof EditableHeaderData, value: any) => {
    const headerKey = getHeaderKey(pListId);
    
    setEditState(prev => ({
      ...prev,
      [headerKey]: {
        ...prev[headerKey],
        [field]: value
      }
    }));
    
    // Mark header as changed
    setChangedHeaders(prev => new Set(prev).add(headerKey));
  };

  const saveHeaderChanges = async (pListId: number) => {
    const headerKey = getHeaderKey(pListId);
    
    if (!changedHeaders.has(headerKey)) {
      return; // No changes to save
    }

    const editData = editState[headerKey];
    if (!editData) return;

    const originalPickList = pickingLists.find(p => p.pListID === pListId);
    if (!originalPickList) return;

    try {
      // Set saving state
      setSaveState(prev => ({ ...prev, [headerKey]: 'saving' }));

      // Build the update payload - send the entire PList object
      const pickListUpdate = {
        ...originalPickList,
        description1: editData.description1,
        description2: editData.description2,
        priority: editData.priority,
        operatorID: editData.operatorID,
        deliveryDate: editData.deliveryDate ? new Date(editData.deliveryDate).toISOString() : null
      };

      await pickingService.updatePickingList(pListId, pickListUpdate);
      
      // Update local state with saved data
      setPickingLists(prev => prev.map(p => 
        p.pListID === pListId 
          ? { ...p, ...pickListUpdate }
          : p
      ));
      
      // Remove from changed headers
      setChangedHeaders(prev => {
        const newSet = new Set(prev);
        newSet.delete(headerKey);
        return newSet;
      });
      
      // Set saved state and clear after animation
      setSaveState(prev => ({ ...prev, [headerKey]: 'saved' }));
      setTimeout(() => {
        setSaveState(prev => {
          const newState = { ...prev };
          delete newState[headerKey];
          return newState;
        });
      }, 2000);
      
    } catch (err) {
      console.error('Error saving header:', err);
      setSaveState(prev => ({ ...prev, [headerKey]: 'error' }));
      alert(`Feil ved lagring: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
      
      // Keep error state for a while
      setTimeout(() => {
        setSaveState(prev => {
          const newState = { ...prev };
          delete newState[headerKey];
          return newState;
        });
      }, 3000);
    }
  };

  const navigateToCell = (pListId: number, field: string) => {
    const cellKey = getCellKey(pListId, field);
    setFocusedCell(cellKey);
    setTimeout(() => {
      const input = inputRefs.current[cellKey];
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  };

  const handleKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>,
    pListId: number,
    field: string,
    listIndex: number,
    fieldIndex: number
  ) => {
    const totalLists = filteredPickingLists.length;

    // Handle Tab and Shift+Tab for horizontal navigation
    if (e.key === 'Tab') {
      e.preventDefault();
      const newFieldIndex = e.shiftKey ? fieldIndex - 1 : fieldIndex + 1;
      
      if (newFieldIndex >= 0 && newFieldIndex < EDITABLE_FIELDS.length) {
        // Move to next/previous field in same row
        const newField = EDITABLE_FIELDS[newFieldIndex];
        navigateToCell(pListId, newField);
      } else if (!e.shiftKey && newFieldIndex >= EDITABLE_FIELDS.length) {
        // Move to first field of next row
        if (listIndex + 1 < totalLists) {
          const newList = filteredPickingLists[listIndex + 1];
          navigateToCell(newList.pListID, EDITABLE_FIELDS[0]);
        }
      } else if (e.shiftKey && newFieldIndex < 0) {
        // Move to last field of previous row
        if (listIndex > 0) {
          const newList = filteredPickingLists[listIndex - 1];
          navigateToCell(newList.pListID, EDITABLE_FIELDS[EDITABLE_FIELDS.length - 1]);
        }
      }
      return;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      
      // Save current header before moving
      await saveHeaderChanges(pListId);
      
      // Move to next/previous list
      const newListIndex = e.key === 'ArrowDown' ? listIndex + 1 : listIndex - 1;
      if (newListIndex >= 0 && newListIndex < totalLists) {
        const newList = filteredPickingLists[newListIndex];
        navigateToCell(newList.pListID, field);
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      
      // Move to next/previous field
      const newFieldIndex = e.key === 'ArrowRight' ? fieldIndex + 1 : fieldIndex - 1;
      if (newFieldIndex >= 0 && newFieldIndex < EDITABLE_FIELDS.length) {
        const newField = EDITABLE_FIELDS[newFieldIndex];
        navigateToCell(pListId, newField);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      await saveHeaderChanges(pListId);
      
      // Move to next list, same field
      if (listIndex + 1 < totalLists) {
        const newList = filteredPickingLists[listIndex + 1];
        navigateToCell(newList.pListID, field);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Revert changes
      const headerKey = getHeaderKey(pListId);
      const originalPickList = pickingLists.find(p => p.pListID === pListId);
      if (originalPickList) {
        setEditState(prev => ({
          ...prev,
          [headerKey]: {
            description1: originalPickList.description1 || '',
            description2: originalPickList.description2 || '',
            priority: originalPickList.priority,
            operatorID: originalPickList.operatorID || '',
            deliveryDate: originalPickList.deliveryDate ? new Date(originalPickList.deliveryDate).toISOString().split('T')[0] : ''
          }
        }));
        setChangedHeaders(prev => {
          const newSet = new Set(prev);
          newSet.delete(headerKey);
          return newSet;
        });
      }
      setFocusedCell(null);
    }
  };

  const handleCellBlur = async (pListId: number, e: React.FocusEvent<HTMLInputElement>) => {
    // Always save when losing focus (whether clicking another cell or outside)
    await saveHeaderChanges(pListId);
    
    // Only clear focused cell if not moving to another input
    const newFocusTarget = e.relatedTarget as HTMLElement;
    if (!newFocusTarget || newFocusTarget.tagName !== 'INPUT') {
      setFocusedCell(null);
    }
  };

  const pickListMatchesSearch = (pickList: PListDto): boolean => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      pickList.pListID.toString().includes(searchLower) ||
      (pickList.description1 || '').toLowerCase().includes(searchLower) ||
      (pickList.description2 || '').toLowerCase().includes(searchLower) ||
      (pickList.operatorID || '').toLowerCase().includes(searchLower)
    );
  };

  const filteredPickingLists = useMemo(() => {
    return pickingLists.filter(pickListMatchesSearch);
  }, [pickingLists, searchTerm]);

  // Delete handlers
  const canDelete = (pickList: PListDto): boolean => {
    // Only status <= 10 can be deleted, and must not be locked
    return pickList.pListStatus <= 10 && !pickList.lockedBy;
  };

  const handleDeleteClick = (pickList: PListDto) => {
    setPListToDelete(pickList);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!pListToDelete) return;

    try {
      setDeletingPListId(pListToDelete.pListID);
      setShowDeleteConfirm(false);

      const result = await pickingService.deletePickingList(pListToDelete.pListID);

      if (result.success) {
        // Remove from local state
        setPickingLists(prev => prev.filter(p => p.pListID !== pListToDelete.pListID));

        // Clean up edit state
        const headerKey = getHeaderKey(pListToDelete.pListID);
        setEditState(prev => {
          const newState = { ...prev };
          delete newState[headerKey];
          return newState;
        });
        setChangedHeaders(prev => {
          const newSet = new Set(prev);
          newSet.delete(headerKey);
          return newSet;
        });

        alert(result.message);
      }
    } catch (err) {
      console.error('Error deleting PList:', err);
      alert(`Feil ved sletting: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
    } finally {
      setDeletingPListId(null);
      setPListToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPListToDelete(null);
  };

  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-green-100 text-green-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-yellow-100 text-yellow-800';
      case 40: return 'bg-green-100 text-green-800';
      case 45: return 'bg-purple-100 text-purple-800';
      case 50: return 'bg-indigo-100 text-indigo-800';
      case 60: return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: number): string => {
    if (priority >= 90) return 'bg-red-100 text-red-800';
    if (priority >= 70) return 'bg-orange-100 text-orange-800';
    if (priority >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getRowClassName = (pListId: number): string => {
    const headerKey = getHeaderKey(pListId);
    const state = saveState[headerKey];
    const isChanged = changedHeaders.has(headerKey);
    
    if (state === 'saved') return 'bg-green-50 hover:bg-green-100 transition-colors duration-200';
    if (state === 'error') return 'bg-red-50 hover:bg-red-100';
    if (isChanged) return 'bg-yellow-50 hover:bg-yellow-100';
    return 'hover:bg-gray-50';
  };

  const renderEditableCell = (
    pListId: number,
    field: EditableField,
    fieldIndex: number,
    listIndex: number,
    type: 'text' | 'number' | 'date' = 'text'
  ) => {
    const headerKey = getHeaderKey(pListId);
    const cellKey = getCellKey(pListId, field);
    const isFocused = focusedCell === cellKey;
    const value = editState[headerKey]?.[field] ?? '';

    return (
      <div className="relative">
        <input
          ref={el => inputRefs.current[cellKey] = el}
          type={type}
          value={value}
          onChange={(e) => handleCellChange(pListId, field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          onFocus={() => handleCellFocus(pListId, field)}
          onKeyDown={(e) => handleKeyDown(e, pListId, field, listIndex, fieldIndex)}
          onBlur={(e) => handleCellBlur(pListId, e)}
          className={`
            w-full px-2 py-1 text-sm
            border rounded
            transition-all duration-150
            ${isFocused 
              ? 'border-blue-500 ring-2 ring-blue-200 bg-white' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 bg-white'
            }
            focus:outline-none
            cursor-pointer
          `}
          style={{ minWidth: type === 'date' ? '120px' : type === 'number' ? '70px' : '120px' }}
        />
        {!isFocused && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Edit3 size={10} className="text-gray-400" />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>Laster plukklister...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Feil ved lasting av plukklister</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadPickingLists}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6 px-6 pt-6">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <ListChecks size={24} />
            <span>Plukklister under arbeid</span>
          </h1>
        </div>
        <p className="text-gray-600 mt-1">
          Oversikt over plukklister med status "Godkjent", "Under arbeid", "Behandlet komplett", "Til pakking" og "Klar for eksport" - {filteredPickingLists.length} av {pickingLists.length} plukklister vises
          {changedHeaders.size > 0 && (
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
              {changedHeaders.size} endring{changedHeaders.size > 1 ? 'er' : ''} ikke lagret
            </span>
          )}
        </p>
        <p className="text-gray-500 text-sm mt-1">
          💡 Tips: Klikk i en celle med blå bakgrunn for å redigere. Bruk piltaster, Tab eller Enter for å navigere. Escape for å avbryte.
        </p>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-lg shadow-sm border mb-4 p-4 mx-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Søk etter plukkliste-ID, beskrivelse, operatør..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={loadPickingLists}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              <span>Oppdater</span>
            </button>
          </div>
        </div>
      </div>

      {/* Picking Lists Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden mx-6 mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                  
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plukkliste ID
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Beskrivelse 1
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Beskrivelse 2
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dato
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Leveringsdato
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Linjer
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vekt
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Prioritet
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plukkstatus
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  <Edit3 size={10} className="inline mr-1" />
                  Operatør
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Låst av
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Slett
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPickingLists.map((pickList, listIndex) => {
                const headerKey = getHeaderKey(pickList.pListID);
                const state = saveState[headerKey];
                const isExpanded = expandedLists.has(pickList.pListID);
                const details = listDetails.get(pickList.pListID);
                const isLoadingDetails = loadingDetails.has(pickList.pListID);
                
                return (
                  <React.Fragment key={pickList.id}>
                    <tr className={getRowClassName(pickList.pListID)}>
                      <td 
                        className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                        onClick={() => toggleListExpansion(pickList.pListID)}
                      >
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronDown size={14} className="text-gray-400" />
                          ) : (
                            <ChevronRight size={14} className="text-gray-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center">
                          {state === 'saving' && <Loader className="animate-spin text-blue-600" size={14} />}
                          {state === 'saved' && <Check className="text-green-600 animate-pulse" size={14} />}
                          {state === 'error' && <X className="text-red-600" size={14} />}
                          {!state && changedHeaders.has(headerKey) && (
                            <span className="flex items-center justify-center w-4 h-4 bg-yellow-400 rounded-full text-white text-xs font-bold">
                              !
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-gray-900">{pickList.pListID}</div>
                      </td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(pickList.pListID, 'description1', 0, listIndex, 'text')}</td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(pickList.pListID, 'description2', 1, listIndex, 'text')}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-900 flex items-center space-x-1">
                          <Calendar size={12} className="text-gray-400" />
                          <span>{pickingService.formatDate(pickList.pListDate)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(pickList.pListID, 'deliveryDate', 4, listIndex, 'date')}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-900 flex items-center space-x-1">
                          <Package size={12} className="text-gray-400" />
                          <span>{pickList.numOfOrderLines || 0}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-900">
                          {pickList.weight ? `${pickList.weight.toFixed(2)} kg` : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(pickList.pListID, 'priority', 2, listIndex, 'number')}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pickList.pListStatus)}`}>
                          {pickList.statusText}
                        </span>
                      </td>
                      <td className="px-3 py-2 bg-blue-50">{renderEditableCell(pickList.pListID, 'operatorID', 3, listIndex, 'text')}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-900 flex items-center space-x-1">
                          {pickList.lockedBy ? (
                            <>
                              <AlertCircle size={12} className="text-yellow-600" />
                              <span className="text-yellow-600 text-xs">{pickList.lockedBy}</span>
                            </>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {canDelete(pickList) ? (
                          <button
                            onClick={() => handleDeleteClick(pickList)}
                            disabled={deletingPListId === pickList.pListID}
                            className={`p-1.5 rounded transition-colors ${
                              deletingPListId === pickList.pListID
                                ? 'bg-gray-100 cursor-wait'
                                : 'hover:bg-red-100 text-red-600 hover:text-red-700'
                            }`}
                            title="Slett plukkliste"
                          >
                            {deletingPListId === pickList.pListID ? (
                              <Loader className="animate-spin" size={14} />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-300" title={pickList.lockedBy ? `Låst av ${pickList.lockedBy}` : 'Kan ikke slettes (status > 10)'}>
                            <Trash2 size={14} />
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* Expanded Lines Section */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={14} className="px-3 py-3 bg-gray-50">
                          {isLoadingDetails ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader className="animate-spin mr-2" size={20} />
                              <span className="text-gray-600">Laster linjer...</span>
                            </div>
                          ) : details ? (
                            <div className="overflow-x-auto">
                              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                                <Package size={16} />
                                <span>Plukkliste-linjer for {pickList.pListID}</span>
                                <span className="text-xs text-gray-500">
                                  ({details.completedLines} av {details.totalLines} fullført - {details.completionPercentage.toFixed(0)}%)
                                </span>
                              </h4>
                              
                              {details.lines.length > 0 ? (
                                <table className="min-w-full text-sm border border-gray-200 rounded">
                                  <thead className="bg-gray-100">
                                    <tr className="border-b border-gray-300">
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Linjenr</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Ordrenr</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Ordre linje</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Varenummer</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Antall</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Reservert</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Plukket</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Gjenstående</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Lokasjon</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Lager</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Status</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Sortering</th>
                                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 uppercase">Fullført</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 bg-white">
                                    {details.lines.map((line) => (
                                      <tr key={line.id} className="hover:bg-gray-50">
                                        <td className="py-2 px-3 text-gray-900 font-medium">{line.pListLineNo}</td>
                                        <td className="py-2 px-3 text-gray-900">{line.orderNo}</td>
                                        <td className="py-2 px-3 text-gray-900">{line.lineNo}</td>
                                        <td className="py-2 px-3 text-gray-900 font-mono text-xs">{line.itemNo || '-'}</td>
                                        <td className="py-2 px-3 text-gray-900">{line.qty.toFixed(2)}</td>
                                        <td className="py-2 px-3 text-gray-900">{line.qtyReserved.toFixed(2)}</td>
                                        <td className="py-2 px-3 text-gray-900">{line.qtyPicked.toFixed(2)}</td>
                                        <td className="py-2 px-3 text-gray-900 font-medium">{line.remainingQty.toFixed(2)}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.binCode || '-'}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.warehouseID || '-'}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.lineStatus}</td>
                                        <td className="py-2 px-3 text-gray-900 text-xs">{line.sorting}</td>
                                        <td className="py-2 px-3 text-center">
                                          {line.isCompleted ? (
                                            <Check className="text-green-600 mx-auto" size={16} />
                                          ) : (
                                            <X className="text-gray-400 mx-auto" size={16} />
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  Ingen linjer funnet for denne plukklisten
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              Kunne ikke laste linjer
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredPickingLists.length === 0 && (
          <div className="text-center py-12">
            <ListChecks size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Ingen plukklister funnet</h3>
            <p className="text-gray-500">
              {pickingLists.length === 0 
                ? 'Ingen plukklister under arbeid funnet.' 
                : 'Ingen plukklister matcher søkekriteriene.'
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Fjern filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && pListToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center space-x-3">
              <Trash2 size={24} />
              <h2 className="text-xl font-semibold">Bekreft sletting</h2>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Er du sikker på at du vil slette plukkliste <strong>{pListToDelete.pListID}</strong>?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">Beskrivelse:</div>
                  <div className="text-gray-900">{pListToDelete.description1 || '-'}</div>
                  <div className="text-gray-500">Antall linjer:</div>
                  <div className="text-gray-900">{pListToDelete.numOfOrderLines || 0}</div>
                  <div className="text-gray-500">Status:</div>
                  <div className="text-gray-900">{pListToDelete.statusText}</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Advarsel:</strong> Denne handlingen kan ikke angres. Plukklisten og alle tilhørende linjer vil bli permanent slettet.
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <Trash2 size={16} />
                <span>Slett plukkliste</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingPickingLists;
